import * as React from 'react';
import { Dialog } from '@headlessui/react';
import { useSpring, animated } from '@react-spring/web';
import { useEffect } from 'react';
import { cn } from '@restworld/utils-common';
export interface ModalLayoutProps {
  children: React.ReactNode;

  /**
   * Whether is open
   */
  isOpen: boolean;

  /**
   * Modal props only used ONLY when layout == modal
   */
  styles?: React.CSSProperties;

  /**
   * Callback when to close it
   */
  onClose: () => void;
}

const AnimatedModal = animated(Dialog);

export function ModalLayout({ isOpen, children, styles, onClose }: ModalLayoutProps) {
  const animation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? `translateY(0)` : `translateY(-200%)`,
  });

  useEffect(() => {
    // 👇 add class to body element
    if (isOpen) {
      document.body.classList.add('dialog-open');
    } else {
      document.body.classList.remove('dialog-open');
    }
  }, [isOpen]);

  const modalWidth = 'fit-content';
  const modalHeight = 'fit-content';

  return (
    <StyledAnimatedModalContainer>
      <StyledAnimatedModal
        open={isOpen}
        onClose={onClose}
        style={{
          ...animation,
        }}
      >
        {/* The backdrop, rendered as a fixed sibling to the panel container */}
        <ModalOverlay onClick={onClose} />

        <ModalContentContainer>
          <ModalContentPanel
            style={{
              width: modalWidth,
              height: modalHeight,
              ...styles,
            }}
          >
            {children}
          </ModalContentPanel>
        </ModalContentContainer>
      </StyledAnimatedModal>
    </StyledAnimatedModalContainer>
  );
}

const StyledAnimatedModalContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('fixed h-screen w-screen', className)} {...props} />
);

const StyledAnimatedModal = ({ className, ...props }: any) => (
  <AnimatedModal className={cn('z-[150] fixed touch-none top-0 h-screen w-screen', className)} {...props} />
);

const ModalOverlay = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('fixed overflow-hidden w-full h-full z-50 bg-gray-300 bg-opacity-30', className)} {...props} />
);

const ModalContentContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center rounded-t-3xl shadow h-full w-full', className)} {...props} />
);

const ModalContentPanel = ({ className, ...props }: any) => (
  <Dialog.Panel
    className={cn(
      'w-full md:min-w-[24rem] md:max-w-lg rounded-2xl bg-white z-[60] left-[40vh]' /* left value won't work without position */,
      className
    )}
    {...props}
  />
);

export default ModalLayout;
